/**
 * Copyright (c) 2026 Lars Jänsch. All Rights Reserved.
 * This software is proprietary and confidential.
 * Unauthorized copying, modification, or distribution is strictly prohibited.
 * See LICENSE file for terms and conditions.
 */

import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { lazy, Suspense, useEffect } from 'react';
import { UIProvider } from './components/ui';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import { AppSettingsProvider, useAppSettings } from './contexts/AppSettingsContext';
import apiService from './services/apiService';
import { refreshPushSubscriptionIfNeeded } from './services/pushService';
import ArchiveViewerHost from './components/archive/ArchiveViewerHost';

// Seiten werden erst bei Bedarf geladen (kleinerer Start, v. a. im Mobilfunknetz).
// Der Service Worker speichert alle Teile vorab – offline funktioniert weiterhin alles.
const HomePage = lazy(() => import('./pages/HomePage'));
const SearchPage = lazy(() => import('./pages/SearchPage'));
const DeviceSelectionPage = lazy(() => import('./pages/DeviceSelectionPage'));
const CartPage = lazy(() => import('./pages/CartPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const DeviceManagementPage = lazy(() => import('./pages/DeviceManagementPage'));
const PartManagementPage = lazy(() => import('./pages/PartManagementPage'));
const UserManagementPage = lazy(() => import('./pages/UserManagementPage'));
const CustomerManagementPage = lazy(() => import('./pages/CustomerManagementPage'));
const CategoryManagementPage = lazy(() => import('./pages/CategoryManagementPage'));
const DocTemplateManagementPage = lazy(() => import('./pages/DocTemplateManagementPage'));
const MaintenancePage = lazy(() => import('./pages/MaintenancePage'));
const DispatcherOrderPage = lazy(() => import('./pages/DispatcherOrderPage'));
const DocumentationPage = lazy(() => import('./pages/DocumentationPage'));
const DocGeneratorPage = lazy(() => import('./pages/DocGeneratorPage'));
const NewsFeedPage = lazy(() => import('./pages/NewsFeedPage'));
const EasyhelpPage = lazy(() => import('./pages/EasyhelpPage'));
const LeaderboardPage = lazy(() => import('./pages/LeaderboardPage'));
const PasswordDatabasePage = lazy(() => import('./pages/PasswordDatabasePage'));
const ShippingMethodsPage = lazy(() => import('./pages/ShippingMethodsPage'));
const ShippingStoragePage = lazy(() => import('./pages/ShippingStoragePage'));
const PermissionsManagementPage = lazy(() => import('./pages/PermissionsManagementPage'));
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage'));
const DocumentArchivePage = lazy(() => import('./pages/DocumentArchivePage'));
const DesignSystemPage = lazy(() => import('./pages/DesignSystemPage'));
const AdminHubPage = lazy(() => import('./pages/AdminHubPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const SearchGapsPage = lazy(() => import('./pages/SearchGapsPage'));

function AppRoutes() {
  const { easyhelpLeaderboardEnabled } = useAppSettings();

  useEffect(() => {
    // Check server configuration on app start
    const checkConfiguration = async () => {
      try {
        await apiService.get('/health');
      } catch (error) {
        // Configuration error will be shown by apiService
        console.error('Configuration check failed:', error);
      }
    };
    checkConfiguration();
    // Sicherstellen dass Push-Subscription noch aktiv ist (iOS setzt diese manchmal zurück)
    refreshPushSubscriptionIfNeeded().catch(() => {});
  }, []);

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register/:token" element={<RegisterPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/datenschutz" element={<Suspense fallback={null}><PrivacyPage /></Suspense>} />
        <Route path="/" element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }>
          <Route index element={<HomePage />} />
          <Route path="devices" element={<DeviceSelectionPage />} />
          <Route path="search" element={<SearchPage />} />
          <Route path="cart" element={<CartPage />} />
          <Route path="dispatcher-orders" element={<DispatcherOrderPage />} />
          <Route path="documentation" element={<DocumentationPage />} />
          <Route path="doc-generator" element={<DocGeneratorPage />} />
          <Route path="news-feed" element={<NewsFeedPage />} />
          <Route path="easyhelp" element={<EasyhelpPage />} />
          <Route
            path="leaderboard"
            element={easyhelpLeaderboardEnabled ? <LeaderboardPage /> : <Navigate to="/" replace />}
          />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="manage-customers" element={<CustomerManagementPage />} />
          <Route path="manage-categories" element={<CategoryManagementPage />} />
          <Route path="manage-doc-templates" element={<DocTemplateManagementPage />} />
          <Route path="manage-devices" element={<DeviceManagementPage />} />
          <Route path="manage-parts" element={<PartManagementPage />} />
          <Route path="maintenance" element={<MaintenancePage />} />
          <Route path="manage-users" element={<UserManagementPage />} />
          <Route path="passwords" element={<PasswordDatabasePage />} />
          <Route path="shipping-methods" element={<ShippingMethodsPage />} />
          <Route path="shipping-storage" element={<ShippingStoragePage />} />
          <Route path="permissions" element={<PermissionsManagementPage />} />
          <Route path="analytics" element={<AnalyticsPage />} />
          <Route path="document-archive" element={<DocumentArchivePage />} />
          <Route path="search-gaps" element={<SearchGapsPage />} />
          <Route
            path="verwaltung"
            element={<AdminHubPage />}
          />
          <Route
            path="design-system"
            element={<DesignSystemPage />}
          />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

function App() {
  return (
    <AppSettingsProvider>
      <UIProvider>
        <AppRoutes />
        <ArchiveViewerHost />
      </UIProvider>
    </AppSettingsProvider>
  );
}

export default App;
